import {
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type Omittable,
  type TextProps,
} from "@salestrak/ui";

import {
  formatDate,
  formatDateShort,
  formatDateTime,
  formatFullDateTime,
  formatNoteDate,
  relativeDayTime,
} from "#app/lib/utils/format-date";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";

/**
 * The abridged label a timestamp renders as. Every rung drops something
 * (the time, the year, or the calendar date itself), and the hover tooltip
 * restores it — one component so no surface shows an abridged time without
 * the full one a hover away.
 *
 *  - "relative" → relative while fresh ("15m ago"), then "Jul 16" / "Sep 2024"
 *  - "long"     → "3 days ago" / "2 weeks ago"
 *  - "compact"  → "3d ago" / "2w ago"
 *  - "short"    → "Jul 16"
 *  - "date"     → "Jul 16, 2026"
 *  - "dateTime" → "Jul 16, 2:30 PM"
 *  - "note"     → "7/16, 2:30 PM"
 *  - "dayTime"  → "Today · 2:30 PM"
 */
export type DateTextFormat =
  | "relative"
  | "long"
  | "compact"
  | "short"
  | "date"
  | "dateTime"
  | "note"
  | "dayTime";

const FORMATTERS: Record<DateTextFormat, (date: string) => string> = {
  relative: (date) => formatRelativeDate(date, "dated"),
  long: (date) => formatRelativeDate(date, "long"),
  compact: (date) => formatRelativeDate(date, "compact"),
  short: formatDateShort,
  date: formatDate,
  dateTime: formatDateTime,
  note: formatNoteDate,
  dayTime: relativeDayTime,
};

/**
 * A timestamp label with the unabridged "Jul 16, 2026, 2:30 PM" in a hover
 * tooltip. Touch has no hover — the plain text stands alone there (Base UI
 * tooltips are hover/focus-driven, so no gate is needed).
 *
 * Defaults fit a table cell: `size={null}` inherits the row's density text
 * size (matching the library `date` column helper), muted, truncating.
 * Other surfaces pass their own rung.
 */
export function DateText({
  date,
  format = "relative",
  fallback = "—",
  size = null,
  variant = "muted",
  weight,
  color,
  truncate = true,
  suffix,
  label,
  hoverInk,
}: Omittable<{
  date: string | null | undefined;
  format?: DateTextFormat;
  /** Rendered (muted, no tooltip) when there's no date. */
  fallback?: string;
  size?: TextProps["size"];
  variant?: TextProps["variant"];
  weight?: TextProps["weight"];
  color?: TextProps["color"];
  truncate?: TextProps["truncate"];
  /** Light the label to body ink on hover (`true`) or with its `group`
   *  ancestor (`"row"`) — the hover that opens the tooltip announces itself
   *  where the label sits in quiet prose (an activity row's time). */
  hoverInk?: TextProps["hoverInk"];
  /** Trailing text inside the same label (" (edited)"). */
  suffix?: string;
  /** Replaces the formatted label — for a reading DERIVED from the date
   *  that no `format` produces (a countdown, "20d"). The tooltip still
   *  carries the full timestamp, which is the whole reason the derived
   *  reading goes through this component. */
  label?: string;
}>) {
  if (!date) {
    return (
      <Text size={size} variant="muted" truncate={truncate}>
        {fallback}
      </Text>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Text
            size={size}
            variant={variant}
            weight={weight}
            color={color}
            truncate={truncate}
            hoverInk={hoverInk}
          >
            {label ?? FORMATTERS[format](date)}
            {suffix}
          </Text>
        }
      />
      <TooltipPopup side="top">{formatFullDateTime(date)}</TooltipPopup>
    </Tooltip>
  );
}
